import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import { leavePalette, cardSx } from "../../Leave/leaveHelpers";

// The redesigned Permission / On Duty / Comp Off screens reuse the exact visual
// tokens the redesigned Leave screen already established, so all of My Zone >
// Apply reads as one system and a palette tweak stays a single edit.
export { leavePalette, cardSx };

// Normalize any attendance_type spelling the API might return ("Comp off",
// "compoff", "On Duty", "onduty", "Permission", "permission") to a single key so
// filtering/matching is spelling- and spacing-tolerant.
export const normType = (value) =>
  (value || "").toString().toLowerCase().replace(/[\s_-]/g, "");

// Map the user-break-context API's `allowed_actions` labels onto the attendance
// tab keys they enable, so the sidebar/tabs only surface what the user may raise.
// "Break" (manual) and "Cancel Break" both feed the single "break" tab.
export const allowedTabKeysFromActions = (allowedActions) => {
  const keys = new Set();
  (Array.isArray(allowedActions) ? allowedActions : []).forEach((action) => {
    const type = normType(action?.label);
    if (type === "permission") keys.add("permission");
    else if (type === "onduty") keys.add("onduty");
    else if (type === "compoff") keys.add("compoff");
    else if (type === "break" || type === "cancelbreak") keys.add("break");
  });
  return keys;
};

// Canonical status bucket used by the status tiles and the status filter.
export const canonicalStatus = (status) => {
  const k = (status || "").trim().toLowerCase();
  if (k === "approved") return "Approved";
  if (k === "pending") return "Pending";
  if (k === "rejected") return "Rejected";
  if (k === "cancelled" || k === "canceled") return "Cancelled";
  return status;
};

// Status pill colour. Falls back to the row's own color_code when the status
// name isn't one of the known four, so custom workflow statuses still show.
export const statusMeta = (status, colorCode) => {
  const key = (status || "").trim().toLowerCase();
  const map = {
    approved: leavePalette.green,
    pending: leavePalette.amber,
    rejected: leavePalette.red,
    cancelled: leavePalette.gray,
    canceled: leavePalette.gray,
  };
  return map[key] || colorCode || leavePalette.blue;
};

// "Applied On" isn't a single guaranteed field. Comp Off rows carry
// requested_date as the "requested for" date (not an applied date), so it is
// deliberately excluded from the fallbacks here. Break/Cancel Break rows
// (ActualSpecialAttendance) send `applied_on` specifically — it was missing
// from this list, so "Applied On" always fell through to "-" for Break.
export const appliedOn = (row) =>
  row?.applied_on ||
  row?.applied_date ||
  row?.created_date ||
  row?.created_at ||
  row?.requested_on ||
  "";

export const formatBreakDuration = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const raw = String(value).trim();
  if (!raw || raw.toLowerCase() === "null") return "-";

  const hhmmMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*(hrs?|hours?|minutes?|mins?))?$/i);
  if (hhmmMatch) {
    return `${hhmmMatch[1].padStart(2, "0")}:${hhmmMatch[2]} Hrs`;
  }

  const minuteMatch = raw.match(/^(\d+(?:\.\d+)?)\s*(minutes?|mins?|m)?$/i);
  if (!minuteMatch) return raw;

  const totalMinutes = Number(minuteMatch[1]);
  if (!Number.isFinite(totalMinutes)) return raw;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} Hrs`;
};

// Per-type presentation config. `matches` decides which records belong to the
// tab; `columns` drives the history table; `guidelines` is static, non-numeric
// policy copy (no fabricated entitlement figures).
export const ATTENDANCE_TYPES = {
  permission: {
    key: "permission",
    label: "Permission",
    title: "Permission",
    subtitle: "Request permission for short-duration personal work during working hours",
    newLabel: "New Request",
    defaultType: "Permission",
    icon: AccessTimeIcon,
    color: leavePalette.blue,
    matches: (row) => normType(row?.attendance_type) === "permission",
    guidelines: [
      "Request permission before the applicable date/time where possible",
      "Provide a clear reason for the request",
      "Permission is subject to manager approval",
    ],
  },
  onduty: {
    key: "onduty",
    label: "On Duty",
    title: "On Duty",
    subtitle: "Regularise time spent on official work away from your usual punch",
    newLabel: "New Request",
    defaultType: "On Duty",
    icon: WorkOutlineIcon,
    color: leavePalette.purple,
    matches: (row) => normType(row?.attendance_type) === "onduty",
    guidelines: [
      "Raise On-Duty for official work done outside normal attendance",
      "Provide the correct date and time window",
      "On-Duty is subject to manager approval",
    ],
  },
  compoff: {
    key: "compoff",
    label: "Comp Off",
    title: "Comp Off",
    subtitle: "Claim compensatory off for working on a week-off or holiday",
    newLabel: "New Request",
    defaultType: "Comp off",
    icon: EventAvailableIcon,
    color: leavePalette.amber,
    matches: (row) => normType(row?.attendance_type) === "compoff",
    guidelines: [
      "Comp Off can be claimed for approved work on a week-off/holiday",
      "Select the date worked and the date you want to avail",
      "Comp Off is subject to manager approval",
    ],
  },
  break: {
    key: "break",
    label: "Apply Break",
    title: "Apply Break",
    subtitle: "Apply Break or cancel an existing break for a selected date",
    newLabel: "New Request",
    defaultType: "Break",
    icon: FreeBreakfastIcon,
    color: "#0D9488",
    // The Break tab lists both manual-break and cancel-break records.
    matches: (row) => {
      const type = normType(row?.attendance_type);
      return type === "break" || type === "cancelbreak";
    },
    // Rows carry either type, so the "Break" column label is per-row rather
    // than the single tab label the other types use.
    typeLabel: (row) =>
      normType(row?.attendance_type) === "cancelbreak"
        ? "Cancel Break"
        : "Apply Break",
    guidelines: [
      "Apply Break or cancel an existing break for the selected date",
      "Provide the correct date and break window",
      "Break requests are subject to manager approval",
    ],
  },
};
